import type { NeoConversationDTO } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import { parseServerDate } from "../../utils/serverDate";

export interface RecentChatGroup {
  key: string;
  title: string;
  items: NeoConversationDTO[];
}

const lastActivity = (conversation: NeoConversationDTO): Date | null => {
  const raw =
    conversation.last_message_at ??
    conversation.updated_at ??
    conversation.created_at;
  if (!raw) return null;
  const date = parseServerDate(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// Local midnight `days` calendar days before `date`. Uses the Date
// constructor rather than subtracting 24h so DST days (23h/25h) don't shift
// the boundary.
const startOfDaysAgo = (date: Date, days: number): number =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - days
  ).getTime();

/** Today / Yesterday / Previous 7 days / Older, newest first, filtered by
 *  title. Input is already sorted newest first. */
export const groupRecentChats = (
  conversations: NeoConversationDTO[],
  query = "",
  now: Date = new Date()
): RecentChatGroup[] => {
  const needle = query.trim().toLowerCase();
  const today = startOfDay(now);
  const yesterday = startOfDaysAgo(now, 1);
  const weekAgo = startOfDaysAgo(now, 7);
  const groups: RecentChatGroup[] = [
    { key: "today", title: "Today", items: [] },
    { key: "yesterday", title: "Yesterday", items: [] },
    { key: "week", title: "Previous 7 days", items: [] },
    { key: "older", title: "Older", items: [] },
  ];

  conversations.forEach((conversation) => {
    const title = conversation.title?.trim() || "New Conversation";
    if (needle && !title.toLowerCase().includes(needle)) return;
    const date = lastActivity(conversation);
    const day = date ? startOfDay(date) : 0;
    const index =
      day >= today ? 0 : day >= yesterday ? 1 : day >= weekAgo ? 2 : 3;
    groups[index].items.push(conversation);
  });

  return groups.filter((group) => group.items.length > 0);
};
